import type { GanttIndicadores } from "@/shared/components/gantt-carga/gantt-carga.constants";
import { useEffect, useMemo, useState } from "react";

import { formatMonthRangeLabel, resolveListEmptyKind } from "@/ds";
import type { FilterValue } from "@/ds";
import { getPersistedUser } from "@/modules/auth";
import {
  applyLocalFilters,
  buildLocalFilterColumns,
  conflitosAbertos,
  EMPTY_LOCAL_FILTERS,
  exportAlocacoesLocalPorObra,
  exportAlocacoesPorLocal,
  filtraLocalObraCancelada,
  hasLocalFilters,
  LOCAL_FILTER,
  OBRA_SITUACAO_CANCELADA,
  useAlocacoesPorLocal,
  useAlocacoesPorObra,
} from "@/modules/carga-de-trabalho";
import type { AlocacoesPorLocal, AlocacoesPorObra } from "@/modules/carga-de-trabalho";
import { buildTimeline, todayIso } from "@/modules/carga-trabalho";
import { describeReportFilters, formatExportedLabel, panelQuery } from "@/modules/export";
import {
  aplicarCenarioLocais,
  aplicarCenarioObrasLocal,
  cargaDeLocais,
  cargaDeObrasLocal,
  conflitosLocais,
  novosConflitos,
  SEM_MARCAS,
} from "@/modules/recursos/simulacao";
import type { AlteracaoSimulada, MarcasSimulacao } from "@/modules/recursos/simulacao";

import {
  AGRUPAMENTO_LOCAL_OPTIONS,
  CARGA_CHIP_TEST_ID_PREFIX,
  CARGA_ESTADO_IDS,
  CARGA_LEGEND_TEST_ID,
  CARGA_TOOLBAR_IDS,
  DEFAULT_AGRUPAMENTO_LOCAL,
  DEFAULT_INDICADORES,
  INDICADORES_OBRA_E_CANCELADA,
  CARGA_LOCAL_SAVED_FILTERS_SCREEN,
  CARGA_LOCAL_SEARCH_PLACEHOLDER,
} from "../carga-de-trabalho.constants";
import type { AgrupamentoLocal } from "../carga-de-trabalho.constants";
import {
  chipsDosFiltros,
  conteudoDoVazio,
  datasDoPeriodo,
  VAZIO_BUSCA,
  VAZIO_FILTRO,
} from "../carga-de-trabalho.utils";
import type {
  ResolucaoLocalSimulada,
  ResolverConflitoNotice,
} from "../components/resolver-conflito-local";
import {
  buildLocalObraRows,
  buildLocalRows,
} from "@/shared/components/gantt-carga/gantt-local-rows";
import { focoDaVisaoPorObras } from "@/shared/components/gantt-carga/gantt-obras-rows";
import type { LocalRowModel } from "@/shared/components/gantt-carga/gantt-local-rows";
import { useCargaExportacao } from "../hooks/use-carga-exportacao";
import { useRotulosDasObras } from "../hooks/use-rotulos-das-obras";
import { useCargaFiltrosSalvos } from "../hooks/use-carga-filtros-salvos";
import { usePeriodoDaAba } from "../hooks/use-carga-periodo";
import { GROUPING_HINT, LEGEND, VAZIO_SEM_ALOCACAO } from "./carga-local.constants";
import type { CargaLocalProps, CargaLocalViewModel } from "./types-carga-local";

/** Carga de Trabalho: perspectiva Local (US-2023), agrupada por Local ou por Obra. */
export function useCargaLocal({
  periodoCompartilhado,
  onPerspectivaChange,
  onNotify,
  cenario = null,
  onEntrarSimulacao,
  onAbrirHistorico,
  onIncluirNaSimulacao,
  onCargaAtual,
}: CargaLocalProps): CargaLocalViewModel {
  const simulando = cenario !== null;
  const [agrupamento, setAgrupamento] = useState<AgrupamentoLocal>(DEFAULT_AGRUPAMENTO_LOCAL);
  const { periodo: periodoMes, escolherPeriodo } = usePeriodoDaAba(periodoCompartilhado);
  const periodo = useMemo(() => datasDoPeriodo(periodoMes), [periodoMes]);
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<FilterValue>(EMPTY_LOCAL_FILTERS);
  const [indicators, setIndicators] = useState<GanttIndicadores>(DEFAULT_INDICADORES);
  const filtrosSalvos = useCargaFiltrosSalvos(CARGA_LOCAL_SAVED_FILTERS_SCREEN, aplicarFiltros);
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);
  const [resolverRow, setResolverRow] = useState<LocalRowModel | null>(null);

  const isLocal = agrupamento === "local";
  const query = { dataInicio: periodo.inicio, dataFim: periodo.fim, busca: search };
  const porLocal = useAlocacoesPorLocal({ ...query, enabled: isLocal });
  const porObra = useAlocacoesPorObra({ ...query, enabled: !isLocal });
  const { status, errorMessage, reload } = isLocal ? porLocal : porObra;

  useEffect(() => {
    if (!onCargaAtual) return;
    if (isLocal) {
      onCargaAtual(porLocal.data ? cargaDeLocais(porLocal.data) : null);
      return;
    }
    onCargaAtual(porObra.data ? cargaDeObrasLocal(porObra.data) : null);
  }, [isLocal, onCargaAtual, porLocal.data, porObra.data]);

  // Na simulação o Gantt mostra os dados com o cenário aplicado (HU 2225).
  const simulado = useMemo((): {
    dados: AlocacoesPorLocal | AlocacoesPorObra | null;
    marcas: MarcasSimulacao;
  } => {
    const base = isLocal ? porLocal.data : porObra.data;
    if (!base || !cenario) return { dados: base, marcas: SEM_MARCAS };
    return isLocal
      ? aplicarCenarioLocais(base as AlocacoesPorLocal, cenario)
      : aplicarCenarioObrasLocal(base as AlocacoesPorObra, cenario);
  }, [isLocal, porLocal.data, porObra.data, cenario]);
  const data = simulado.dados;

  // As opções vêm dos dados sem filtro; a filtragem do painel é feita sobre eles, no cliente.
  const filterColumns = useMemo(() => buildLocalFilterColumns(data), [data]);
  const rotulosDasObras = useRotulosDasObras(filterColumns);
  const filteredData = useMemo(
    () => (data ? applyLocalFilters(data, filterValue) : null),
    [data, filterValue],
  );
  const filteredPorLocal = isLocal ? (filteredData as AlocacoesPorLocal | null) : null;
  const filteredPorObra = isLocal ? null : (filteredData as AlocacoesPorObra | null);

  const today = useMemo(() => todayIso(), []);
  // A escala é o período escolhido: o cabeçalho mostra só os meses dele (as barras além das pontas são cortadas).
  // Por Local abre no início do período; por Obra, na 1ª alocação visível dele.
  const foco = useMemo(() => {
    if (!filteredPorObra) return periodo.inicio;
    const alocacoes = filteredPorObra.obras.flatMap((obra) =>
      obra.alocacoes.map(({ inicio, fim }) => ({
        inicio,
        fim,
        obraCancelada: obra.situacao === OBRA_SITUACAO_CANCELADA,
      })),
    );
    return focoDaVisaoPorObras(alocacoes, periodo, indicators.cancelada);
  }, [filteredPorObra, periodo, indicators.cancelada]);
  const timeline = useMemo(() => buildTimeline(periodo, foco), [periodo, foco]);

  const localRows = useMemo(
    () =>
      status === "success" && filteredPorLocal
        ? buildLocalRows(filteredPorLocal, timeline, indicators, today)
        : [],
    [status, filteredPorLocal, timeline, indicators, today],
  );

  const obraRows = useMemo(
    () =>
      status === "success" && filteredPorObra
        ? buildLocalObraRows(filteredPorObra, timeline, indicators, today)
        : [],
    [status, filteredPorObra, timeline, indicators, today],
  );

  const itemCount = isLocal ? localRows.length : obraRows.length;
  // Sem dados do core, ou com tudo oculto pelos indicadores, não há o que desenhar.
  const isEmpty = status === "success" && data !== null && itemCount === 0;

  const hasSearch = search.trim() !== "";
  const hasFilters = hasLocalFilters(filterValue);
  const emptyKind = resolveListEmptyKind(hasSearch, hasFilters);
  const periodoLabel = formatMonthRangeLabel(periodoMes);
  const reportFilters = describeReportFilters(
    search,
    filterColumns,
    filterValue,
    { periodo: periodoLabel },
    rotulosDasObras,
  );
  const printTitle = isLocal ? "Carga de Trabalho — Locais" : "Carga de Trabalho — Obras (locais)";

  const { exportView, exportar } = useCargaExportacao({
    fileName: "carga-local",
    isEmpty,
    loadFile: (format) => {
      const exportFn = isLocal ? exportAlocacoesPorLocal : exportAlocacoesLocalPorObra;
      return exportFn({
        dataInicio: periodo.inicio,
        dataFim: periodo.fim,
        busca: search,
        format,
        filtros: reportFilters,
        painel: panelQuery(filterValue, [
          { key: LOCAL_FILTER.local, param: "filtroLocal", multiple: true },
          { key: LOCAL_FILTER.obra, param: "filtroObra", multiple: true },
          { key: LOCAL_FILTER.status, param: "filtroStatus", multiple: true },
          { key: LOCAL_FILTER.situacao, param: "filtroSituacao", multiple: false },
          { key: LOCAL_FILTER.conflito, param: "filtroConflito", multiple: false },
        ]),
      });
    },
    auditoria: {
      recurso: isLocal ? "alocacoes/locais" : "alocacoes/obras/locais",
      quantidade: itemCount,
      inicio: periodo.inicio,
      fim: periodo.fim,
      busca: search,
    },
    printTitle,
  });

  // Cada indicador desligado some da legenda: a Cancelada só aparece com o indicador ligado.
  const legendItems = LEGEND.filter((item) => indicators[item.key as keyof GanttIndicadores]);

  /** Resolver Conflito na simulação (HU 2224): remanejar e ignorar viram alterações do cenário. */
  function resolucaoSimulada(row: LocalRowModel): ResolucaoLocalSimulada | undefined {
    const atual = isLocal ? (simulado.dados as AlocacoesPorLocal | null) : null;
    if (!cenario || !onIncluirNaSimulacao || !atual) return undefined;
    const incluir = onIncluirNaSimulacao;
    const local = atual.locais.find((item) => item.id === row.key);
    const recurso = { id: row.key, codigo: local?.codigo ?? "", nome: row.title };
    // Os dados já têm o cenário: o conflito novo é o que esta alteração acrescenta a eles.
    const comConflitos = (alteracao: AlteracaoSimulada) =>
      incluir({
        alteracao,
        conflitosNovos: novosConflitos(
          conflitosLocais(atual),
          conflitosLocais(aplicarCenarioLocais(atual, { alteracoes: [alteracao] }).dados),
        ),
      });

    return {
      remanejar: ({ alocacao, substituto, justificativa }) =>
        comConflitos({
          acao: "remanejar-local",
          obra: alocacao.obra,
          alocacaoId: alocacao.id,
          periodo: { inicio: alocacao.inicio, fim: alocacao.fim },
          anterior: recurso,
          novo: { id: substituto.id, codigo: substituto.codigo, nome: substituto.nome },
          justificativa,
        }),
      ignorar: ({ conflito, justificativa }) =>
        comConflitos({
          acao: "ignorar-conflito-local",
          local: recurso,
          obras: [conflito.a.obra, conflito.b.obra],
          alocacaoIds: [conflito.a.id, conflito.b.id],
          conflito: { inicio: conflito.inicio, fim: conflito.fim },
          justificativa,
        }),
    };
  }

  function conflitoResolvido(notice: ResolverConflitoNotice) {
    setResolverRow(null);
    onNotify?.(notice);
    // O bff devolve o cronograma já com o remanejamento / o conflito tratado (em verde).
    reload();
  }

  /** Filtrar Situação = Cancelada liga o indicador Cancelada, senão a linha ficaria vazia. */
  function aplicarFiltros(next: FilterValue) {
    setFilterValue(next);
    if (filtraLocalObraCancelada(next))
      setIndicators((current) => ({ ...current, cancelada: true }));
  }

  function limparTudo() {
    setSearch("");
    setFilterValue(EMPTY_LOCAL_FILTERS);
  }

  function removerChip(key: string) {
    setFilterValue((current) => ({ ...current, [key]: [] }));
  }

  /** Pelo menos um indicador precisa continuar ativo: o modal do DS já barra, e aqui a regra fica garantida. */
  function salvarIndicadores(next: GanttIndicadores) {
    if (next.obra || next.cancelada) setIndicators(next);
    setIndicatorsOpen(false);
  }

  return {
    estado: {
      status,
      errorMessage,
      onRetry: reload,
      isEmpty,
      emptyKind,
      emptyContent: conteudoDoVazio(emptyKind, {
        vazio: VAZIO_SEM_ALOCACAO,
        busca: VAZIO_BUSCA,
        filtro: VAZIO_FILTRO,
      }),
      onClearEmpty: emptyKind === "sem-resultados-busca" ? () => setSearch("") : limparTudo,
      ids: CARGA_ESTADO_IDS,
    },
    capaImpressao: {
      title: printTitle,
      generatedLabel: formatExportedLabel(getPersistedUser()?.name),
      filters: reportFilters,
      simulando,
    },
    legendaImpressao: {
      items: legendItems,
      simulando,
      forPrint: true,
      simuladoPreenchido: true,
    },
    toolbar: {
      ids: CARGA_TOOLBAR_IDS,
      search,
      onSearch: setSearch,
      searchLabel: "Buscar por Local ou obra",
      searchPlaceholder: CARGA_LOCAL_SEARCH_PLACEHOLDER,
      searchMaxLength: 100,
      periodo: periodoMes,
      onPeriodo: escolherPeriodo,
      agrupamento: {
        value: agrupamento,
        options: AGRUPAMENTO_LOCAL_OPTIONS,
        hint: GROUPING_HINT,
        onChange: (value) => setAgrupamento(value as AgrupamentoLocal),
      },
      filtros: {
        definitions: filterColumns,
        value: filterValue,
        onChange: aplicarFiltros,
        ...filtrosSalvos,
      },
      perspectiva: "local",
      onPerspectiva: onPerspectivaChange,
      menu: {
        simulando,
        onEntrarSimulacao,
        onAbrirHistorico,
        canExport: status === "success" && !isEmpty,
        onExportar: exportView.open,
        onIndicadores: () => setIndicatorsOpen(true),
      },
    },
    chips: chipsDosFiltros(filterColumns, filterValue, CARGA_CHIP_TEST_ID_PREFIX, rotulosDasObras),
    removerChip,
    legenda: {
      items: legendItems,
      testId: CARGA_LEGEND_TEST_ID,
      simulando,
      alteracoes: cenario?.alteracoes.length ?? 0,
      simuladoPreenchido: true,
    },
    ganttLocal:
      status === "success" && localRows.length > 0
        ? {
            rows: localRows,
            timeline,
            today,
            // Na simulação, sem como incluir no cenário, não há resolução (ela gravaria).
            onResolver: simulando && !onIncluirNaSimulacao ? undefined : setResolverRow,
            marcas: simulado.marcas,
          }
        : null,
    ganttLocalObras:
      status === "success" && obraRows.length > 0
        ? { rows: obraRows, timeline, today, marcas: simulado.marcas }
        : null,
    resolver: resolverRow
      ? {
          local: {
            id: resolverRow.key,
            nome: resolverRow.title,
            cidade: resolverRow.cidade,
            uf: resolverRow.uf,
          },
          conflitos: conflitosAbertos(resolverRow.conflitos),
          data: isLocal ? (simulado.dados as AlocacoesPorLocal | null) : null,
          periodo,
          today,
          onResolved: simulando ? () => setResolverRow(null) : conflitoResolvido,
          onClose: () => setResolverRow(null),
          simulacao: resolucaoSimulada(resolverRow),
        }
      : null,
    exportacao: {
      aberta: exportView.isOpen,
      exportando: exportView.isExporting,
      exportar,
      fechar: exportView.close,
    },
    indicadores: {
      opcoes: INDICADORES_OBRA_E_CANCELADA,
      aberto: indicatorsOpen,
      valor: indicators,
      salvar: salvarIndicadores,
      cancelar: () => setIndicatorsOpen(false),
    },
  };
}
